"use client"

import { useState, useEffect } from "react"
import { FiEdit, FiTrash2, FiPlus } from "react-icons/fi"
import { getPlans, createPlan, updatePlan, deletePlan } from "../utils/api"

export default function Subscription() {
  // Subscription plans data
  const [plans, setPlans] = useState([])
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingPlan, setEditingPlan] = useState(null)
  const [formData, setFormData] = useState({
    title: "",
    price: "",
    profileLimit: "",
    tenure: "30",
    type: "Membership",
    crmIncluded: false,
    cloudCredits: false,
    matrimonyType: "Entrepreneur",
    features: [""],
  })

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null)

  // Fetch plans on mount
  useEffect(() => {
    fetchPlans()
  }, [])

  const fetchPlans = async () => {
    try {
      const data = await getPlans()
      setPlans(data)
    } catch (err) {
      console.error(err)
    }
  }

  // Handle all input changes
  const handleInputChange = (e) => {
    const { name, value, type: inputType, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: inputType === "checkbox" ? checked : value,
    }))
  }

  // Features array handling
  const handleFeatureChange = (idx, value) => {
    setFormData((prev) => {
      const features = [...prev.features]
      features[idx] = value
      return { ...prev, features }
    })
  }

  const addFeature = () => setFormData((prev) => ({ ...prev, features: [...prev.features, ""] }))

  const removeFeature = (idx) =>
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== idx),
    }))

  // Submit create or update
  const handleSubmit = async (e) => {
    e.preventDefault()
    const { title, price, profileLimit, tenure, type, crmIncluded, cloudCredits, matrimonyType, features } = formData

    if (!title || !price) {
      alert("Please fill in title and price")
      return
    }

    if (type === "Matrimony" && profileLimit === "") {
      alert("Please set profile limit for Matrimony plans")
      return
    }

    const payload = {
      title,
      price: Number.parseInt(price, 10),
      tenure: Number.parseInt(tenure, 10),
      type,
      ...(type === "Membership" && { crmIncluded }),
      ...(type === "Membership" && { cloudCredits }),
      ...(type === "Matrimony" && {
        profileLimit: Number.parseInt(profileLimit, 10),
        matrimonyType,
      }),
      features: features.filter((f) => f.trim()),
    }

    try {
      if (editingPlan?._id) {
        await updatePlan(editingPlan._id, payload)
      } else {
        await createPlan(payload)
      }
      await fetchPlans()
      closeDialog()
    } catch (err) {
      console.error(err)
    }
  }

  // Open form for edit
  const handleEdit = (plan) => {
    setEditingPlan(plan)
    setFormData({
      title: plan.title,
      price: plan.price.toString(),
      profileLimit: plan.profileLimit?.toString() || "",
      tenure: plan.tenure?.toString() || "30",
      type: plan.type || "Membership",
      crmIncluded: !!plan.crmIncluded,
      cloudCredits: !!plan.cloudCredits,
      matrimonyType: plan.matrimonyType || "Entrepreneur",
      features: plan.features.length ? plan.features : [""],
    })
    setIsDialogOpen(true)
  }

  // Delete flow
  const confirmDelete = (id) => setDeleteTarget(id)
  const handleConfirmDelete = async () => {
    try {
      await deletePlan(deleteTarget)
      await fetchPlans()
    } catch (err) {
      console.error(err)
    } finally {
      setDeleteTarget(null)
    }
  }

  // Dialog controls
  const resetForm = () =>
    setFormData({
      title: "",
      price: "",
      profileLimit: "",
      tenure: "30",
      type: "Membership",
      crmIncluded: false,
      cloudCredits: false,
      matrimonyType: "Entrepreneur",
      features: [""],
    })

  const closeDialog = () => {
    resetForm()
    setEditingPlan(null)
    setIsDialogOpen(false)
  }

  const openDialog = () => {
    resetForm()
    setEditingPlan(null)
    setIsDialogOpen(true)
  }

  const tenureOptions = [
    { value: 30, label: "1 Month" },
    { value: 90, label: "3 Months" },
    { value: 180, label: "6 Months" },
    { value: 365, label: "1 Year" },
  ]

  return (
    <div className="bg-white min-h-screen">
      <div className="w-full mx-auto p-6">
        {/* Header */}
        <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                  />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Subscription Plans</h1>
                <p className="text-gray-500 text-sm">Manage subscription plans and pricing</p>
              </div>
            </div>

            <button
              onClick={openDialog}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center gap-2"
            >
              <FiPlus className="text-lg" />
              Add Plan
            </button>
          </div>
        </div>

        {/* Plans Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">S.NO</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tenure
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {plans.length ? (
                plans.map((plan, idx) => (
                  <tr key={plan._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{idx + 1}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{plan.title}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">₹ {plan.price}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 inline-flex text-xs leading-5 font-medium rounded-full ${
                          plan.type === "Membership" ? "bg-blue-100 text-blue-800" : "bg-purple-100 text-purple-800"
                        }`}
                      >
                        {plan.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {tenureOptions.find((o) => o.value === plan.tenure)?.label}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                      <div className="flex items-center justify-center gap-3">
                        <button onClick={() => handleEdit(plan)} className="text-blue-600 hover:text-blue-900">
                          <FiEdit className="h-5 w-5" />
                        </button>
                        <button onClick={() => confirmDelete(plan._id)} className="text-red-600 hover:text-red-900">
                          <FiTrash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                    No plans found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {isDialogOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4"
          onClick={closeDialog}
        >
          <div
            className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              {editingPlan ? "Edit Plan" : "Add Plan"}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Title & Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="Membership">Membership</option>
                  <option value="Matrimony">Matrimony</option>
                </select>
              </div>

              {/* CRM Included toggle for Membership */}
              {formData.type === "Membership" && (
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="crmIncluded"
                    id="crmIncluded"
                    checked={formData.crmIncluded}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="crmIncluded" className="text-sm text-gray-700">
                    CRM Included
                  </label>
                </div>
              )}

              {formData.type === "Membership" && (
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="cloudCredits"
                    id="cloudCredits"
                    checked={formData.cloudCredits}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="cloudCredits" className="text-sm text-gray-700">
                    Cloud Credits Included
                  </label>
                </div>
              )}

              {/* Tenure */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tenure</label>
                <select
                  name="tenure"
                  value={formData.tenure}
                  onChange={handleInputChange}
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {tenureOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Matrimony-only fields */}
              {formData.type === "Matrimony" && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Profile Limit</label>
                    <input
                      type="number"
                      name="profileLimit"
                      value={formData.profileLimit}
                      onChange={handleInputChange}
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.profileLimit === "0" ? "Unlimited profiles" : `Up to ${formData.profileLimit} profiles`}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Matrimony Type</label>
                    <select
                      name="matrimonyType"
                      value={formData.matrimonyType}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="Entrepreneur">Entrepreneur</option>
                      <option value="Non-entrepreneur">Non-entrepreneur</option>
                    </select>
                  </div>
                </>
              )}

              {/* Features */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Features</label>
                {formData.features.map((feat, i) => (
                  <div key={i} className="flex items-center mb-2">
                    <input
                      type="text"
                      value={feat}
                      onChange={(e) => handleFeatureChange(i, e.target.value)}
                      placeholder={`Feature ${i + 1}`}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    {formData.features.length > 1 && (
                      <button type="button" onClick={() => removeFeature(i)} className="ml-2 text-red-500">
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={addFeature} className="mt-2 text-sm text-blue-600 hover:text-blue-800">
                  + Add Feature
                </button>
              </div>

              {/* Save / Cancel */}
              <div className="flex gap-2 pt-4">
                <button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-md">
                  Save
                </button>
                <button
                  type="button"
                  onClick={closeDialog}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setDeleteTarget(null)}
        >
          <div className="bg-white rounded-lg w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Confirm Delete</h3>
            <p className="text-sm text-gray-500 mb-4">
              Are you sure you want to delete this plan? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}